import { useState, useCallback, useEffect, useRef } from 'react'
import type { BaseQuizData, QuizSolveResult } from '../../../types/legacyQuiz'

export interface UseExplanationQuizOptions {
  quizId: string | number
  quiz: BaseQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  isViewSolved?: boolean
  initialAnswers?: string[]
  onAnswerChange?: (answers: string[]) => void
  showCorrectAnswersInitially?: boolean
}

export function useExplanationQuiz({
  quizId,
  quiz,
  onSolve,
  solveResult,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  onAnswerChange,
  showCorrectAnswersInitially,
}: UseExplanationQuizOptions) {
  const [startTime] = useState(() => Date.now())
  const [isShowingCorrectAnswers, setIsShowingCorrectAnswers] = useState(
    showCorrectAnswersInitially ?? isViewSolved,
  )

  const isSolved = solveResult !== null && solveResult !== undefined

  const computedInitialAnswer = initialAnswersProp?.[0] ?? ''
  const [answer, setAnswer] = useState(computedInitialAnswer)
  const onAnswerChangeRef = useRef(onAnswerChange)

  const canSubmit = answer.trim().length > 0

  useEffect(() => {
    onAnswerChangeRef.current = onAnswerChange
  }, [onAnswerChange])

  useEffect(() => {
    onAnswerChangeRef.current?.([answer])
  }, [answer])

  const handleSubmit = useCallback(() => {
    if (!canSubmit || isSolved) return
    const elapsedTime = Date.now() - startTime
    onSolve?.(quizId, [answer], quiz.vars, elapsedTime)
  }, [answer, canSubmit, isSolved, onSolve, quizId, quiz.vars, startTime])

  return {
    answer,
    setAnswer,
    isSolved,
    canSubmit,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleSubmit,
    quiz,
    quizId,
    solveResult,
    isViewSolved,
  }
}
